import React, { useState, useEffect } from 'react';
import { RouteComponentProps, useParams } from '@reach/router';
import { capitalize } from 'lodash';
import { COLORS, EARLY, MAIN_CONTENT_SIZE, NOT_FOUND } from '../../../constants';
import { FIRST_EXEMPLAR_ROUTE, SAVED_PROFORMA, WRITING_PROFORMA } from '../../../constants/navigation';
import { Wrapper } from '../../../components/Wrapper';
import { MainContent } from '../../../components/MainContent';
import { Related } from '../../../components/BottomNavigation/Related';
import { StyledRelative, RelatedWrapper, StyledContentWrapper } from '../../../components/Styled';
import { ExemplarCombined, RelatedTexts, RelatedObject, TextTypes } from '../../../types';
import { FullScreen } from '../../../components/FullScreen';
import { StyledWordContextProvider } from '../../../contexts/styledWordContext';
import { DrawingContextProvider } from '../../../contexts/drawingContext';
import { ExemplarProvider as LaterProvider } from '../../../contexts/laterContext';
import { ExemplarProvider as EmergentProvider } from '../../../contexts/emergentContext';
import { WordsPageProvider } from '../../../contexts/wordsPageContext';
import { TextPresentationPageProvider } from '../../../contexts/textPresentationPageContext';
import { proformasAPI } from '../../../API/proformas';
import { FlexBehavior, FlexJustify, Spinner } from 'react-magma-dom';
import { ContactUsError } from '../../../components/ContactUsError';
import { useQuery } from '../../../hooks/useQuery';
import { ImageProvider } from '../../../contexts/imageContext';
import { StyledCenteredSpinnerWrapper } from '../../../components/Styled/StyledWrappers';
import { getStageColors } from '../../../utils';
import { ComingSoon } from '../../../components/ComingSoon';
import { getStageAvailability } from '../../../utils/envs';

interface IRoute extends RouteComponentProps {
  id?: number;
  type?: string;
  screen?: string;
}

export const Proforma = (props: IRoute): JSX.Element => {
  const { type, screen, navigate } = props;

  const [isLoading, setLoading] = useState(false);
  const [proforma, setProforma] = useState<ExemplarCombined>();
  const [related, setRelated] = useState<RelatedTexts>({ default: [], saved: [] });
  const [isError, setError] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);

  const { id } = useParams();
  const textType = useQuery('textType');
  const stage = useQuery('stage')?.toLowerCase() || EARLY;

  useEffect(() => {
    setIsComingSoon(!getStageAvailability(stage as string));
    setLoading(true);
    (async () => {
      try {
        const { data } =
          type === SAVED_PROFORMA
            ? await proformasAPI.getSavedProforma(id)
            : await proformasAPI.getProforma(capitalize(stage), textType || TextTypes.recount);

        setProforma({ ...data.data.content, ...{ id: data.data.id } });
        setRelated(data.data.related);
      } catch (error) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const addSavedToRelated = (savedText: RelatedObject) => {
    setRelated({ ...related, saved: [savedText, ...related.saved] });
  };

  const removeSavedFromRelated = (id: number) => {
    setRelated({ ...related, saved: related.saved.filter(related => related.id !== id) });
  };

  if (type && ![SAVED_PROFORMA].includes(type)) {
    navigate && navigate(`/${NOT_FOUND}`);
  }

  if (isComingSoon) {
    return <ComingSoon stageNotReady />;
  }

  if (isLoading) {
    const { main } = getStageColors(stage);

    return (
      <StyledCenteredSpinnerWrapper behavior={FlexBehavior.container} justify={FlexJustify.center}>
        <Spinner color={main} size={50} />
      </StyledCenteredSpinnerWrapper>
    );
  }

  if (isError) {
    return <ContactUsError />;
  }

  if (!proforma) {
    return <></>;
  }

  return (
    <>
      <StyledRelative>
        <ImageProvider>
          <FullScreen screenToDisplay={screen} navigate={navigate} page={FIRST_EXEMPLAR_ROUTE[stage]}>
            <Wrapper color={COLORS.blackOlive}>
              <StyledWordContextProvider>
                <DrawingContextProvider>
                  <LaterProvider>
                    <EmergentProvider>
                      <TextPresentationPageProvider>
                        <WordsPageProvider>
                          <MainContent
                            assets={[]}
                            stage={stage}
                            page={WRITING_PROFORMA}
                            exemplar={proforma}
                            exemplarId={id}
                            type={type}
                            navigate={navigate}
                            addSavedToRelated={addSavedToRelated}
                          />
                        </WordsPageProvider>
                      </TextPresentationPageProvider>
                    </EmergentProvider>
                  </LaterProvider>
                </DrawingContextProvider>
              </StyledWordContextProvider>
            </Wrapper>
          </FullScreen>
        </ImageProvider>
      </StyledRelative>
      {!(screen === 'full-screen') ? (
        <RelatedWrapper className="testing">
          <StyledContentWrapper width={MAIN_CONTENT_SIZE.S[0]}>
            <Related
              stage={stage}
              page={WRITING_PROFORMA}
              related={related}
              type={type}
              id={id}
              removeSavedFromRelated={removeSavedFromRelated}
            />
          </StyledContentWrapper>
        </RelatedWrapper>
      ) : null}
    </>
  );
};
